import {Trans} from '@lingui/macro';
import {first} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DateTimeFormatterBase} from '../../../../../srcCommon/formatters/dateTime/dateTimeFormatterBase';
import {isActivityInConversationRelevant} from '../../../../helpers/models/conversationHelpers';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../../models/api/activityModel';
import {UnreadBannerBase, UnreadBannerPositionsEnum} from './unreadBannerBase';

/*
 * Props.
 */

interface UnreadBannerAboveProps {
  onUnreadBannerClick: HandlerOf<number | undefined>;
  activities: ReadonlyArray<ActivityModel>;
  firstUnreadActivity?: ActivityModel;
  lastActivity?: ActivityModel;
  hasOnlyLastMessageInView: boolean;
  lastVisibleActivityDate: number;
  hasUnreadActivityAbove: boolean;
  userId: number;
}

/*
 * Component.
 */

export const UnreadBannerAbove: FC<UnreadBannerAboveProps> = (props) => {
  const [newUnreadActivities, setNewUnreadActivities] = useState<ReadonlyArray<ActivityModel>>([]);
  const [isUnreadAboveDismissed, setIsUnreadAboveDismissed] = useState(false);

  const {
    activities,
    hasUnreadActivityAbove,
    hasOnlyLastMessageInView,
    onUnreadBannerClick,
    firstUnreadActivity,
    lastActivity,
    lastVisibleActivityDate,
    userId,
  } = props;

  // Check for new activities above while on the conversation
  useEffect(() => {
    if (!activities.length) {
      return;
    }

    if (!lastActivity || !hasOnlyLastMessageInView || lastVisibleActivityDate >= lastActivity.date) {
      setNewUnreadActivities([]);
      return;
    }

    // Find unread activities that were received after the page loaded below the visible view
    const unreadActivities = activities.filter(
      (activity) =>
        activity.date > lastVisibleActivityDate &&
        isActivityInConversationRelevant(activity) &&
        !isCurrentUserAuthor(activity, userId),
    );

    setNewUnreadActivities(unreadActivities);
  }, [activities, hasOnlyLastMessageInView, lastActivity, lastVisibleActivityDate, userId]);

  const onDismissBanner = useCallback(() => {
    setIsUnreadAboveDismissed(true);
    setNewUnreadActivities([]);
  }, []);

  const onNavigateToUnread = useCallback(() => {
    const firstNewUnreadActivity = first(newUnreadActivities);
    const firstNewUnreadActivityId = firstNewUnreadActivity && firstNewUnreadActivity.id;
    const firstInitialUnreadActivityId = firstUnreadActivity && firstUnreadActivity.id;

    // If we have new unseen messages above that were received while we were on the conversation go to the first one
    // otherwise scroll to the first initial unread message
    onUnreadBannerClick(firstNewUnreadActivityId || firstInitialUnreadActivityId);
    onDismissBanner();
  }, [newUnreadActivities, firstUnreadActivity, onUnreadBannerClick, onDismissBanner]);

  const hasUnreadAbove = hasUnreadActivityAbove && !isUnreadAboveDismissed;
  const hasNewUnreadActivities = newUnreadActivities.length > 0;
  const isVisible = hasUnreadAbove || hasNewUnreadActivities;
  if (!isVisible) {
    return null;
  }

  return (
    <UnreadBannerBase
      currentPosition={UnreadBannerPositionsEnum.TOP}
      label={renderUnreadBannerLabel(hasUnreadAbove, newUnreadActivities.length, firstUnreadActivity)}
      onNavigateToUnreadClick={onNavigateToUnread}
      onDismissBannerClick={onDismissBanner}
    />
  );
};

/*
 * Helpers.
 */

function renderUnreadBannerLabel(
  hasInitialUnreadAbove: boolean,
  newUnreadCount: number,
  firstUnreadActivity?: ActivityModel,
) {
  if (newUnreadCount && !hasInitialUnreadAbove) {
    return <Trans id="unread-banner-above-newestunread">{newUnreadCount} unread</Trans>;
  }

  if (firstUnreadActivity) {
    return (
      <Trans id="unread-banner-above-dateunread">
        {`Jump to first unread (${renderDateTime(firstUnreadActivity.date)})`}
      </Trans>
    );
  }

  return <Trans id="unread-banner-above-unread">Jump to first unread</Trans>;
}

function renderDateTime(unreadDate: number) {
  if (!unreadDate) {
    return '';
  }

  const date = new Date(unreadDate);
  return (
    <DateTimeFormatterBase
      value={date}
      format={{
        month: 'short',
        day: 'numeric',
      }}
    />
  );
}

function isCurrentUserAuthor(activity: ActivityModel, userId: number) {
  if (
    activity.visualType !== ActivityVisualTypesEnum.MESSAGE &&
    activity.visualType !== ActivityVisualTypesEnum.COMMENT
  ) {
    return false;
  }

  return activity.authorId && activity.authorId === userId;
}
